/* eslint-disable no-param-reassign */
/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Column,
  DataRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Row,
  Typography,
} from '@tempmee/react';
import moment from 'moment';
import { UserProfession } from '../../../../../enums/UserProfession';
import { states } from '../../../../commonComponents/StatePicker';
import { StateCredentialFileV2 } from './StateCredentialFileV2';
import { ConfirmedNameSection } from './ConfirmedNameSection';
import { CsDecisionReasonField } from './CsDecisionReasonField';
import { CredentialCard } from './CredentialCard';
import {
  CREDENTIAL_FIELD_WIDTH,
  CredentialDateRow,
  CredentialInfoRow,
  CredentialStatusSelect,
  CredentialTextField,
} from './CredentialFormFields';
import { showErrorMessage } from '../../../../../utils';
import {
  DECISION_REASON_FIELD,
  isDecisionReasonCredential,
  V2Type,
  authorizationShape,
  getCredentialKey,
  getStatusConfig,
  isConfirmedNameCredential,
  monitoredCredentialShape,
  professionShape,
} from './credentialV2Utils';
import { useCredentialForm } from './hooks/useCredentialForm';

// At most two cards per row; one when a card would be narrower than 400px.
const CARD_GRID_COLUMNS = 'repeat(auto-fill, minmax(max(400px, calc(50% - 8px)), 1fr))';

export const AuthorizationCredentialDetail = ({
  authorization,
  allProfessions,
  onSave,
  onDelete,
  monitoredCredentials,
}) => {
  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const {
    editMode,
    auth,
    credentials,
    confirmedName,
    attested,
    setAttested,
    handleEdit,
    handleCancel,
    handleSave,
    updateCred,
    updateDecisionReason,
    updateConfirmedName,
    updateReferenceByIndex,
    getFieldError,
    setNoExpiration,
    getNoExpiration,
  } = useCredentialForm({ authorization, onSave });

  const { profession, state: authState, status } = auth || {};

  const professionName =
    allProfessions?.find((p) => p.code === profession)?.name ||
    UserProfession.getName(profession) ||
    profession;

  const derivedStatus = status;

  const statesFiltered = states.filter((s) => s.abbreviation === authState);
  const licVerificationUrl = statesFiltered.length > 0 ? statesFiltered[0].licVerificationUrl : '';

  const allAttested = attested;

  const closeDeleteDialog = () => setShowDeletePopup(false);

  const renderInfoCard = () => {
    const statusConfig = getStatusConfig(derivedStatus);
    return (
      <Card outline>
        <DataRow
          label="License Status:"
          value={<span className={statusConfig.className}>{statusConfig.label}</span>}
        />
        <DataRow label="State:" value={authState} />
        <DataRow label="Profession:" value={professionName} divider={false} />
      </Card>
    );
  };

  // One card per authorization: the state licence, or the DA/EFDA certification.
  const renderConfirmedNameSection = (cred) =>
    isConfirmedNameCredential(cred) && (
      <ConfirmedNameSection
        editMode={editMode}
        confirmedName={confirmedName}
        onChange={updateConfirmedName}
      />
    );

  const renderNumberFields = (cred) => {
    const numberData = cred?.data?.number;
    const credNumberError = getFieldError(cred, 'credential_number');
    const issuedError = getFieldError(cred, 'issued_at');
    const expiredError = getFieldError(cred, 'expired_at');

    return (
      <Column align="center" width="100%" pb={10}>
        <Column width={CREDENTIAL_FIELD_WIDTH} py={5}>
          <CredentialTextField
            id={`credential-number-${getCredentialKey(cred)}`}
            label="License Number"
            value={numberData?.credential_number}
            editMode={editMode}
            error={credNumberError}
            onChange={(value) => {
              updateCred(cred, (c) => {
                if (!c.data) c.data = {};
                if (!c.data.number) c.data.number = {};
                c.data.number.credential_number = value;
              });
            }}
          />
        </Column>

        <CredentialDateRow
          label="Issued Date"
          value={cred?.issued_at}
          editMode={editMode}
          error={issuedError}
          onChange={(value) => {
            updateCred(cred, (c) => {
              c.issued_at = value;
            });
          }}
        />

        <CredentialDateRow
          label="Expiration Date"
          value={cred?.expired_at}
          editMode={editMode}
          error={expiredError}
          onChange={(value) => {
            updateCred(cred, (c) => {
              c.expired_at = value;
            });
          }}
        />

        {(() => {
          if (!cred?.credential?.includes('state_license')) return null;
          const credMonitored = monitoredCredentials.find((mc) => mc.credential_id === cred.id);
          if (!credMonitored?.last_verified_at) return null;
          return (
            <CredentialInfoRow
              label="Last Date Checked"
              value={moment(credMonitored.last_verified_at).format('YYYY-MM-DD')}
            />
          );
        })()}

        {renderConfirmedNameSection(cred)}
      </Column>
    );
  };

  const renderArtifactDateFields = (cred) => {
    const issued_at = cred?.issued_at;
    const expired_at = cred?.expired_at;
    const noExpiration = getNoExpiration(cred);
    const issuedError = getFieldError(cred, 'issued_at');
    const expiredError = getFieldError(cred, 'expired_at');

    return (
      <Column align="center" width="100%" pt={12}>
        <CredentialDateRow
          label="Issued Date"
          value={issued_at}
          editMode={editMode}
          error={issuedError}
          onChange={(value) => {
            updateCred(cred, (c) => {
              c.issued_at = value;
            });
          }}
        />

        <CredentialDateRow
          label="Expiration Date"
          value={expired_at}
          editMode={editMode}
          error={expiredError}
          disabled={noExpiration}
          onChange={(value) => {
            updateCred(cred, (c) => {
              c.expired_at = value;
            });
          }}
        />

        {editMode && (
          <Box width={CREDENTIAL_FIELD_WIDTH} px={3} pt={3} pb={1}>
            <Checkbox
              size="sm"
              label="No expiration date"
              checked={noExpiration}
              onChange={(checked) => setNoExpiration(cred, checked)}
            />
          </Box>
        )}
      </Column>
    );
  };

  // DA/EFDA certificate only. Saved only through the artifact update, so a credential that
  // does not exist yet has none.
  const renderDecisionReason = (cred) =>
    cred?.id &&
    isDecisionReasonCredential(cred) && (
      <Column align="center" width="100%" pt={4}>
        <Column width={CREDENTIAL_FIELD_WIDTH} px={3}>
          <CsDecisionReasonField
            editMode={editMode}
            value={cred[DECISION_REASON_FIELD]}
            error={getFieldError(cred, DECISION_REASON_FIELD)}
            onChange={(value) => updateDecisionReason(cred, value)}
          />
        </Column>
      </Column>
    );

  const renderArtifactSection = (cred) => {
    const artifactFiles = cred?.data?.artifact?.files || [];
    const hasFiles = artifactFiles.length > 0;

    const renderFiles = () => {
      if (!editMode && !hasFiles) {
        return (
          <Center py={5}>
            <Typography variant="body2" className="text-neutral-500">
              No file uploaded
            </Typography>
          </Center>
        );
      }

      // Same column as the rows below, with room under the card title.
      return (
        <Box width={CREDENTIAL_FIELD_WIDTH} pt={4}>
          <StateCredentialFileV2
            title={cred.credential_name}
            editMode={editMode}
            files={artifactFiles}
            onFilesChange={(newFiles) => {
              updateCred(cred, (c) => {
                if (!c.data) c.data = {};
                if (!c.data.artifact) c.data.artifact = {};
                c.data.artifact.files = newFiles;
              });
            }}
          />
        </Box>
      );
    };

    return (
      <Column align="center" width="100%">
        {renderFiles()}
        {renderArtifactDateFields(cred)}
        {renderDecisionReason(cred)}
        {renderConfirmedNameSection(cred)}
      </Column>
    );
  };

  const renderNpiFields = (cred) => {
    const npiData = cred?.data?.npi;
    const npiError = getFieldError(cred, 'npi_number');

    return (
      <Column align="center" width="100%" pb={10}>
        <Column width={CREDENTIAL_FIELD_WIDTH} py={5}>
          <CredentialTextField
            id={`npi-number-${getCredentialKey(cred)}`}
            label="NPI Number"
            value={npiData?.npi_number}
            editMode={editMode}
            error={npiError}
            maxLength={10}
            onChange={(value) => {
              updateCred(cred, (c) => {
                if (!c.data) c.data = {};
                if (!c.data.npi) c.data.npi = {};
                c.data.npi.npi_number = value;
              });
            }}
          />
        </Column>
      </Column>
    );
  };

  const renderCredentialContent = (cred, referenceIndex) => {
    if (cred.type === V2Type.Number) {
      return renderNumberFields(cred);
    }

    if (cred.type === V2Type.Artifact) {
      return renderArtifactSection(cred);
    }

    if (cred.type === V2Type.Npi) {
      return renderNpiFields(cred);
    }

    if (cred.type === V2Type.Reference) {
      const refData = cred?.data?.reference;
      const emailError = getFieldError(cred, 'contact_email');

      return (
        <Column align="center" width="100%" pb={10}>
          <Column width={CREDENTIAL_FIELD_WIDTH} gap={4} py={5}>
            <CredentialTextField
              id={`office-name-${getCredentialKey(cred)}-${referenceIndex}`}
              label="Office Name"
              value={refData?.office_name}
              editMode={editMode}
              onChange={(value) => {
                updateReferenceByIndex(referenceIndex, (c) => {
                  if (!c.data) c.data = {};
                  if (!c.data.reference) c.data.reference = {};
                  c.data.reference.office_name = value;
                });
              }}
            />
            <CredentialTextField
              id={`contact-email-${getCredentialKey(cred)}-${referenceIndex}`}
              label="Contact Email"
              value={refData?.contact_email}
              editMode={editMode}
              error={emailError}
              inputMode="email"
              onChange={(value) => {
                updateReferenceByIndex(referenceIndex, (c) => {
                  if (!c.data) c.data = {};
                  if (!c.data.reference) c.data.reference = {};
                  c.data.reference.contact_email = value;
                });
              }}
            />
          </Column>

          <CredentialDateRow
            label="Start Date"
            value={refData?.start_date}
            editMode={editMode}
            onChange={(value) => {
              updateReferenceByIndex(referenceIndex, (c) => {
                if (!c.data) c.data = {};
                if (!c.data.reference) c.data.reference = {};
                c.data.reference.start_date = value;
              });
            }}
          />

          <CredentialDateRow
            label="End Date"
            value={refData?.end_date}
            editMode={editMode}
            onChange={(value) => {
              updateReferenceByIndex(referenceIndex, (c) => {
                if (!c.data) c.data = {};
                if (!c.data.reference) c.data.reference = {};
                c.data.reference.end_date = value;
              });
            }}
          />
        </Column>
      );
    }

    return null;
  };

  const renderCredentialStatusRow = (cred) => {
    const statusConfig = getStatusConfig(cred.status);
    return (
      <Row
        justify="between"
        align="center"
        gap={2}
        px={2}
        py={2}
        className="border-b border-solid border-divider"
      >
        <Typography variant="body2">License Status:</Typography>
        {editMode ? (
          <CredentialStatusSelect
            value={cred.status}
            onChange={(value) => {
              updateCred(cred, (c) => {
                c.status = value;
              });
            }}
          />
        ) : (
          <Typography variant="body2" className={statusConfig.className}>
            {statusConfig.label}
          </Typography>
        )}
      </Row>
    );
  };

  const getCredentialSubtitle = (cred) => {
    if (cred?.type === V2Type.Npi) {
      return 'Enter NPI Number';
    }
    if (cred?.type === V2Type.Number && cred?.credential?.includes('state_license')) {
      return 'Enter License Number';
    }
    if (cred?.type === V2Type.Artifact) {
      return `Example: ${cred.credential_name || ''}`;
    }
    return null;
  };

  return (
    <Column gap={4} py={2}>
      <Center>
        <Box width="50%">{renderInfoCard()}</Box>
      </Center>

      <Grid columns={CARD_GRID_COLUMNS} gap={4}>
        {credentials.map((cred, index) => {
          const isNpi = cred?.type === V2Type.Npi;
          const isReference = cred?.type === V2Type.Reference;
          const referenceIndex = isReference
            ? credentials.filter((c, i) => c?.type === V2Type.Reference && i < index).length
            : 0;

          // Lookup on every credential: NPI uses the national registry (same for everyone),
          // all others use the state licensing board. Hidden only when the state has no URL.
          const npiLookupUrl = 'https://npiregistry.cms.hhs.gov/search';
          const lookupUrl = isNpi ? npiLookupUrl : licVerificationUrl || null;
          const lookupCopyValue = isNpi
            ? cred?.data?.npi?.npi_number || ''
            : cred?.data?.number?.credential_number || '';
          const isRequired = isReference
            ? referenceIndex === 0 && cred.is_required
            : cred.is_required;

          return (
            <CredentialCard
              key={cred.id || `${cred.credential}-${cred.type}-${index}`}
              statusRow={renderCredentialStatusRow(cred)}
              title={isReference ? cred.credential_name || 'Reference' : cred.credential_name}
              requirement={isRequired ? 'Required(*)' : '(Optional)'}
              subtitle={isReference ? null : getCredentialSubtitle(cred)}
              lookup={lookupUrl ? { url: lookupUrl, copyValue: lookupCopyValue } : null}
            >
              {renderCredentialContent(cred, referenceIndex)}
            </CredentialCard>
          );
        })}
      </Grid>

      <Center pt={4}>
        {!editMode ? (
          <Row gap={4}>
            {/* Equal, roomy widths: a narrow button is harder to land on with a mouse. */}
            <Button color="primary" className="w-32" onClick={handleEdit}>
              Edit
            </Button>
            <Button
              color="error"
              variant="outlined"
              className="w-32"
              onClick={() => setShowDeletePopup(true)}
            >
              Delete
            </Button>
          </Row>
        ) : (
          <Column align="center" gap={3}>
            <Box className="max-w-md">
              <Checkbox
                size="sm"
                label="The credentials provided belong to the same professional that is listed on the government ID of this account."
                checked={attested}
                onChange={setAttested}
              />
            </Box>
            <Row gap={4}>
              <Button
                color="primary"
                className="w-32"
                onClick={() => {
                  if (!allAttested) {
                    showErrorMessage({
                      message: 'Confirm that the credentials provided belong to this professional.',
                      position: 'bottom-right',
                    });
                    return;
                  }
                  handleSave();
                }}
              >
                Save
              </Button>
              <Button variant="outlined" color="secondary" className="w-32" onClick={handleCancel}>
                Cancel
              </Button>
            </Row>
          </Column>
        )}
      </Center>

      <Dialog open={showDeletePopup} onClose={closeDeleteDialog} size="medium">
        <DialogTitle onClose={closeDeleteDialog}>
          <Typography variant="h6">Delete professional credential</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this professional credential?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={closeDeleteDialog}>
            Cancel
          </Button>
          <Button
            color="error"
            onClick={() => {
              onDelete?.(auth?.id);
              closeDeleteDialog();
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Column>
  );
};

AuthorizationCredentialDetail.propTypes = {
  authorization: PropTypes.shape(authorizationShape).isRequired,
  allProfessions: PropTypes.arrayOf(PropTypes.shape(professionShape)),
  /** Receives the edited authorization; may resolve with `{ failures }` (see saveCredentials). */
  onSave: PropTypes.func.isRequired,
  /** Receives the authorization id once the delete is confirmed. */
  onDelete: PropTypes.func,
  monitoredCredentials: PropTypes.arrayOf(PropTypes.shape(monitoredCredentialShape)),
};

AuthorizationCredentialDetail.defaultProps = {
  allProfessions: [],
  onDelete: undefined,
  monitoredCredentials: [],
};
